import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, AreaChart, Area } from "recharts";
import { statusesForZone, trFold, hubsForZone, useSaleStatuses, SALE_STATUS_HEX } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FolderKanban, Gavel, Trophy, Banknote, Users, Ruler, Files, Timer, CalendarClock, FileDown, Wallet } from "lucide-react";

const STATUS_COLORS = { "Sonuçlanan": "#059669", "Aktif İhale": "#2563eb", "Karar Bekleyen": "#ef4444", "Teklifsiz Kapanan": "#f59e0b", "İhalesiz": "#cbd5e1" };
const fmtN = (n) => (n == null ? "-" : Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 }));
const fmtTotals = (totals) => Object.entries(totals || {}).filter(([, v]) => v > 0).map(([c, v]) => `${fmtN(v)} ${c}`).join(" + ") || "-";

const KPI = ({ icon: Icon, label, value, sub, tid, accent }) => {
  const len = String(value ?? "").length;
  const sizeCls = len > 12 ? "text-lg" : len > 9 ? "text-xl" : len > 6 ? "text-2xl" : "text-3xl";
  return (
    <div className="border border-slate-200 bg-white p-4 flex items-start justify-between gap-2 min-w-0" data-testid={tid}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 break-words">{label}</div>
        <div className={`font-heading ${sizeCls} font-extrabold tracking-tighter mt-1.5 break-all ${accent || ""}`}>{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5 break-words">{sub}</div>}
      </div>
      <div className="bg-slate-100 p-1.5 text-slate-600 shrink-0"><Icon size={13} /></div>
    </div>
  );
};

const SaleTrackCard = ({ counts }) => {
  const saleStatuses = useSaleStatuses();
  const total = saleStatuses.reduce((a, s) => a + (counts[s] || 0), 0);
  const paid = counts["Ödeme Alındı"] || 0;
  const pct = total > 0 ? Math.round((paid / total) * 100) : null;
  const pieData = saleStatuses.map((s) => ({ name: s, value: counts[s] || 0, color: SALE_STATUS_HEX[s] || "#64748b" })).filter((d) => d.value > 0);
  return (
    <div className="border border-slate-200 bg-white p-4 flex items-center gap-4 min-w-0" data-testid="sum-kpi-sale-track">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Satış Takibi</div>
        <div className="font-heading text-3xl font-extrabold tracking-tighter mt-1.5 text-emerald-700" data-testid="sale-track-pct">
          {pct != null ? `%${pct}` : "-"}
          {pct != null && <span className="text-sm font-bold text-slate-400 ml-1.5">Ödeme Alındı</span>}
        </div>
        <div className="flex items-center gap-2 mt-1.5 text-xs whitespace-nowrap">
          <span className="font-bold text-emerald-700" data-testid="sale-track-sold">{paid} Ödeme Alındı</span>
          <span className="text-slate-300">/</span>
          <span className="font-bold text-slate-600" data-testid="sale-track-pending">{total} Takipte</span>
        </div>
        <div className="h-1.5 bg-slate-100 mt-2.5 flex overflow-hidden" data-testid="sale-track-bar">
          {total > 0 && pieData.map((d) => (
            <div key={d.name} title={`${d.name}: ${d.value}`} className="h-full transition-all duration-500" style={{ width: `${(d.value / total) * 100}%`, background: d.color }} />
          ))}
        </div>
      </div>
      <div className="shrink-0 w-[72px] h-[72px] overflow-hidden">
        {total > 0 ? (
          <PieChart width={72} height={72}>
            <Pie data={pieData} dataKey="value" nameKey="name" cx={35} cy={35} innerRadius={20} outerRadius={34} paddingAngle={pieData.length > 1 ? 3 : 0} stroke="none" isAnimationActive={false}>
              {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v} proje`, n]} wrapperStyle={{ fontSize: 11, zIndex: 10 }} />
          </PieChart>
        ) : (
          <div className="w-full h-full border border-dashed border-slate-200 flex items-center justify-center text-[9px] text-slate-400 text-center leading-tight">Veri yok</div>
        )}
      </div>
    </div>
  );
};

const Card = ({ title, children, tid, className = "" }) => (
  <div className={`border border-slate-200 bg-white p-5 ${className}`} data-testid={tid}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">{title}</div>
    {children}
  </div>
);

export default function ExecutiveSummaryPage() {
  const saleStatuses = useSaleStatuses();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [stFilter, setStFilter] = useState("all");
  const [hubFilter, setHubFilter] = useState("all");
  const [saleFilter, setSaleFilter] = useState("all");
  const [hubs, setHubs] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [prodSum, setProdSum] = useState(null);

  useEffect(() => {
    api.get("/admin/production-summary", { params: tab !== "all" ? { zone: tab } : {} }).then((r) => setProdSum(r.data)).catch(() => setProdSum(null));
  }, [tab]);

  useEffect(() => {
    api.get("/settings/hubs").then((r) => setHubs(r.data.hubs || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (stFilter !== "all") params.status = stFilter;
    if (hubFilter !== "all") params.hub = hubFilter;
    if (saleFilter !== "all") params.sale = saleFilter;
    api.get("/admin/executive-summary", { params }).then((r) => setData(r.data)).catch(() => setData({ zones: {} }));
  }, [stFilter, hubFilter, saleFilter]);

  if (!data) return <div className="text-sm text-slate-500 p-8">Yükleniyor…</div>;
  const z = data.zones?.[tab];
  if (!z) return <div className="text-sm text-slate-500 p-8">Veri yok</div>;

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const params = {};
      if (stFilter !== "all") params.status = stFilter;
      if (hubFilter !== "all") params.hub = hubFilter;
      if (saleFilter !== "all") params.sale = saleFilter;
      if (tab !== "all") params.zone = tab;
      const res = await api.get("/admin/executive-summary/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yonetici-ozeti-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF oluşturulamadı");
    } finally {
      setPdfBusy(false);
    }
  };

  const sc = z.status_counts || {};
  const hc = z.hub_counts || {};
  const slc = z.sale_counts || {};
  const saleChips = [
    { key: "all", label: "Tümü", count: Object.values(slc).reduce((a, b) => a + b, 0) },
    ...saleStatuses.map((s) => ({ key: s, label: s, count: slc[s] || 0 })),
    { key: "none", label: "Atanmamış", count: slc.none || 0 },
  ];
  const statusChips = [
    { key: "all", label: "Tümü", count: Object.values(sc).reduce((a, b) => a + b, 0) },
    ...statusesForZone(tab === "all" ? null : tab).map((st) => ({ key: st, label: st, count: sc[st] || 0 })),
    { key: "none", label: "Atanmamış", count: sc.none || 0 },
  ];
  const zoneHubs = hubsForZone(hubs, tab === "all" ? null : tab);
  const hubChips = [
    { key: "all", label: "Tümü", count: Object.values(hc).reduce((a, b) => a + b, 0) },
    ...zoneHubs.map((h) => ({ key: h, label: h, count: hc[h] || 0 })),
    { key: "none", label: "Atanmamış", count: hc.none || 0 },
  ];

  const statusData = z.status_dist.filter((s) => s.value > 0);
  const supData = z.top_suppliers.map((s) => ({ ...s, label: s.company.length > 22 ? s.company.slice(0, 22) + "…" : s.company }));
  const typeData = z.type_dist.map((t) => ({ ...t, tutar: t.totals?.TRY || 0 }));
  const tryTotal = z.kpis.totals?.TRY || 0;
  const otherTotals = Object.entries(z.kpis.totals || {}).filter(([c, v]) => c !== "TRY" && v > 0);
  const ot = z.offer_totals || {};
  const otList = ["TRY", "USD", "EUR"].filter((c) => ot[c] > 0).map((c) => `${fmtN(ot[c])} ${c === "TRY" ? "TL" : c}`);
  const prodChartData = prodSum ? prodSum.statuses.map((s) => ({ name: s, count: prodSum.counts[s] || 0 })) : [];
  const prodAssigned = prodSum ? prodSum.total - (prodSum.none || 0) : 0;

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Rapor</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Yönetici Özeti</h1>
        </div>
        <Button onClick={downloadPdf} disabled={pdfBusy} data-testid="summary-pdf-btn"
          className="rounded-none h-10 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
          <FileDown size={15} /> {pdfBusy ? "Hazırlanıyor..." : "PDF İndir"}
        </Button>
      </div>

      <div className="space-y-3">
        <div className="flex gap-0 border-b border-slate-200" data-testid="summary-zone-tabs">
          {[["all", "Tümü"], ["Blue Zone", "Blue Zone"], ["Green Zone", "Green Zone"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => { setTab(k); setStFilter("all"); setHubFilter("all"); setSaleFilter("all"); }}
              data-testid={`summary-tab-${k === "all" ? "all" : k.toLowerCase().replace(" ", "-")}`}
              className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors duration-150 ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {[
            ["Statü", stFilter, setStFilter, statusChips, "summary-status-filter"],
            ...(zoneHubs.length > 0 ? [["Hub", hubFilter, setHubFilter, hubChips, "summary-hub-filter"]] : []),
            ["Durum", saleFilter, setSaleFilter, saleChips, "summary-sale-filter"],
          ].map(([lbl, val, setVal, chips, tid]) => (
            <label key={tid} className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{lbl}:</span>
              <select
                value={val}
                onChange={(e) => setVal(e.target.value)}
                data-testid={tid}
                className={`h-9 border bg-white px-3 text-xs font-bold max-w-[240px] ${val !== "all" ? "border-slate-900 text-slate-900" : "border-slate-200 text-slate-600"}`}
              >
                {chips.map((f) => <option key={f.key} value={f.key}>{f.label} ({f.count})</option>)}
              </select>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="summary-kpis">
        <KPI icon={FolderKanban} label="Toplam Proje" value={z.kpis.projects} tid="sum-kpi-projects" />
        <KPI icon={Gavel} label="İhaleye Açılan" value={z.kpis.tendered} tid="sum-kpi-tendered" accent="text-blue-600" />
        <KPI icon={Trophy} label="Sonuçlanan" value={z.kpis.concluded} tid="sum-kpi-concluded" accent="text-emerald-600" />
        <KPI icon={Banknote} label="Toplam Tutar (TL)" value={tryTotal ? fmtN(tryTotal) : "-"} sub={otherTotals.length ? otherTotals.map(([c, v]) => `+ ${fmtN(v)} ${c}`).join(", ") : null} tid="sum-kpi-total" accent="text-emerald-700" />
        <KPI icon={Users} label="Kazanan Tedarikçi" value={z.kpis.suppliers} tid="sum-kpi-suppliers" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="summary-averages">
        <SaleTrackCard counts={slc} />
        <KPI icon={Wallet} label="Teklif Tutarı Toplamı" value={otList[0] || "-"} sub={otList.slice(1).join(", ") || null} tid="sum-kpi-offer-totals" accent="text-blue-700" />
        <KPI icon={Ruler} label="m² Başına Ort. Maliyet (TL)" value={z.averages.per_m2 != null ? fmtN(z.averages.per_m2) : "-"} tid="sum-avg-m2" />
        <KPI icon={Files} label="İhale Başına Ort. Teklif" value={z.averages.avg_offers_per_tender} tid="sum-avg-offers" />
        <KPI icon={Timer} label="Ort. Sonuçlanma Süresi" value={z.averages.avg_days_to_conclude != null ? `${z.averages.avg_days_to_conclude} gün` : "-"} tid="sum-avg-days" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Proje Durum Dağılımı" tid="summary-status-chart">
          {statusData.length === 0 ? <p className="text-sm text-slate-400">Veri yok</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
                  {statusData.map((s) => <Cell key={s.name} fill={STATUS_COLORS[s.name] || "#94a3b8"} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} proje`, n]} />
                <Legend iconType="square" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="En Çok Kazanan Tedarikçiler (İlk 10)" tid="summary-suppliers-chart">
          {supData.length === 0 ? <p className="text-sm text-slate-400">Henüz sonuçlanan ihale yok</p> : (
            <ResponsiveContainer width="100%" height={Math.max(260, supData.length * 34)}>
              <BarChart data={supData} layout="vertical" margin={{ left: 8, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n, p) => [`${v} ihale — ${fmtTotals(p.payload.totals)}`, "Kazanılan"]} />
                <Bar dataKey="wins" fill="#059669" barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Proje Türü Dağılımı" tid="summary-type-chart">
          {typeData.length === 0 ? <p className="text-sm text-slate-400">Veri yok</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={typeData} margin={{ right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="r" orientation="right" tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : fmtN(v))} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => (n === "Tutar (TL)" ? [fmtN(v) + " TL", n] : [v, n])} />
                <Legend iconType="square" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="l" dataKey="count" name="Proje Sayısı" fill="#2563eb" barSize={22} />
                <Bar yAxisId="r" dataKey="tutar" name="Tutar (TL)" fill="#059669" barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Haftalık Aktivite (Son 12 Hafta)" tid="summary-timeline-chart">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={z.timeline} margin={{ right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend iconType="square" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="requests" name="Açılan İhale" stroke="#2563eb" fill="#2563eb" fillOpacity={0.12} strokeWidth={2} />
              <Area type="monotone" dataKey="offers" name="Gelen Teklif" stroke="#059669" fill="#059669" fillOpacity={0.12} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Üretim İlerlemesi" tid="summary-production">
        {!prodSum || prodAssigned === 0 ? (
          <p className="text-sm text-slate-400">Henüz üretim durumu atanmış proje yok</p>
        ) : (
          <div className="space-y-5">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={prodChartData} margin={{ right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={55} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [`${v} proje`, "Proje"]} />
                <Bar dataKey="count" barSize={26}>
                  {prodChartData.map((d) => <Cell key={d.name} fill={d.name === "Atanmamış" ? "#cbd5e1" : "#6366f1"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="space-y-3">
              {prodSum.statuses.filter((s) => (prodSum.groups[s] || []).length > 0).map((s) => (
                <div key={s} data-testid={`prod-group-${trFold(s).replace(/\s+/g, "-")}`}>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-indigo-700 bg-indigo-50/70 border border-indigo-100 px-3 py-1.5 mb-1.5">
                    {s} ({prodSum.groups[s].length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {prodSum.groups[s].map((p) => (
                      <Link
                        key={p.id}
                        to={`/admin/projeler/${p.id}`}
                        data-testid={`prod-chip-${p.id}`}
                        className="px-2 py-1 text-xs font-semibold border border-slate-200 bg-white hover:border-indigo-400 hover:text-indigo-700 transition-colors duration-150"
                        title={p.zone || ""}
                      >
                        {p.name}{p.stand_no ? <span className="text-slate-400 font-normal"> · {p.stand_no}</span> : null}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              <div className="text-xs text-slate-400" data-testid="prod-unassigned-count">Üretim durumu atanmamış: {prodSum.none} proje</div>
            </div>
          </div>
        )}
      </Card>

      <Card title="Kazandırılan Projeler" tid="summary-won-projects">
        {(z.won_projects || []).length === 0 ? (
          <p className="text-sm text-slate-400">Henüz kazandırılan proje yok</p>
        ) : (
          ["Blue Zone", "Green Zone", "Ofisler", "-"].filter((zn) => (z.won_projects || []).some((w) => w.zone === zn)).map((zn) => {
            const items = z.won_projects.filter((w) => w.zone === zn);
            return (
              <div key={zn} className="mb-5 last:mb-0" data-testid={`won-zone-${zn}`}>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50/70 border border-emerald-100 px-3 py-1.5 mb-1">
                  {zn === "-" ? "Bölgesiz" : zn} ({items.length})
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]">
                    <tbody>
                      {items.map((w, i) => (
                        <tr key={`${w.id}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150" data-testid={`won-row-${w.request_id}`}>
                          <td className="px-3 py-2 font-semibold">
                            <Link to={`/admin/teklifler/${w.request_id}`} className="hover:underline">{w.name}</Link>
                          </td>
                          <td className="px-3 py-2 text-slate-500">{w.stand_no}</td>
                          <td className="px-3 py-2 text-emerald-700 font-semibold">{w.winner}</td>
                          <td className="px-3 py-2 text-right font-bold tabular-nums whitespace-nowrap">{w.price != null ? `${fmtN(w.price)} ${w.currency}` : "-"}</td>
                          <td className="px-3 py-2 text-right text-xs text-slate-500 whitespace-nowrap">
                            {w.date ? new Date(w.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </Card>

      <Card title="Yaklaşan Son Tarihler (7 Gün)" tid="summary-upcoming">
        {z.upcoming.length === 0 ? <p className="text-sm text-slate-400">Önümüzdeki 7 gün içinde sona erecek ihale yok</p> : (
          <div className="divide-y divide-slate-100">
            {z.upcoming.map((u) => (
              <Link key={u.id} to={`/admin/teklifler/${u.id}`} className="flex items-center gap-3 py-2.5 hover:bg-slate-50 px-2 -mx-2 transition-colors duration-150" data-testid={`upcoming-${u.id}`}>
                <CalendarClock size={14} className="text-amber-600 shrink-0" />
                <span className="text-sm font-semibold truncate">{u.title}</span>
                <span className="ml-auto text-xs text-slate-500 shrink-0">{u.offer_count} teklif</span>
                <span className="text-xs font-bold text-amber-700 shrink-0">
                  {new Date(u.deadline + "T00:00:00").toLocaleDateString("tr-TR", { day: "2-digit", month: "long" })}{u.deadline_time ? ` ${u.deadline_time}` : ""}
                </span>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
